import { React, useEffect, useState, useContext, Suspense } from "react";
import { Provider } from "react-redux";
import { Outlet, Route, Routes, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Transition, TransitionGroup } from "react-transition-group";
import { validateLogin, RefreshToken } from "@cisco/spaces-dash-lib-login";
import { AdminAppCenter, AppDetails, AppList } from "@cisco/spaces-dash-app-appcenter";
import Layout from "../layout/layout";
import globalStore from "../../redux/store";
import { I18nContext } from "../../contexts/i18nContext";
import PartnerHome from "../partner-home/partner-home";
import PartnerOnboarding from "../partner-onboarding/partner-onboarding";
import AppActivationSandbox from "../app-activation-sandbox/app-activation-sandbox";
import FirehoseTrace from "../firehose-trace/firehose-trace";
import UserManagement from "../user-management/user-management";
import OauthValidation from "../oauth-validation/oauth-validation";
import * as PartnerHomeService from "../../service/partner-dashboard-service";
import loginI18nResource from "../../locale/en.json";
import { PropertiesContext } from "../../contexts/propertiesContext";
import "./app.scss";
import LoadingProvider from "../../providers/LoadingProvider";

function App(props) {
  const { appData } = props;
  const i18n = useContext(I18nContext);
  const { properties } = useContext(PropertiesContext);
  const [showBaseComponent, setShowBaseComponent] = useState(false);
  const [stepSelected, setStepSelected] = useState(1);
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [selectedPartnerApp, setSelectedPartnerApp] = useState(null);
  const [documentationLink, setDocumentationLink] = useState(null);
  const [activationCount, setActivationCount] = useState(0);
  const [activationMap, setActivationMap] = useState({});
  const [partnerFirstOnBoardMessage, setPartnerFirstOnBoardMessage] = useState(false);
  const location = useLocation();
  const defaultDocumentationLink = "https://www-author3.cisco.com/c/en/us/td/docs/wireless/spaces/partner-dashboard/basic/b-dnas-pd.html";

  const onError = () => {
    navigate("/login");
  };

  const navigateToAppDetails = (app) => {
    setSelectedPartnerApp(app);
    navigate(`partner/sandbox/app/details/${app.appId}`);
  };

  const navigateTo = (path) => {
    navigate(path);
  };

  const selectStep = (step) => {
    setStepSelected(step);
  };

  const prepareActivationMap = (activationMapArray) => {
    const actMap = {};
    activationMapArray.forEach((element) => {
      actMap[element.appId] = element.count;
    });
    setActivationMap(actMap);
  };

  const getAppLevelCumulativeActivation = () => {
    PartnerHomeService.getAppLevelCumulativeActivation()
      .then((data) => {
        if (data.status) {
          if (data.data && data.data.cumulativeActivation > -1) {
            setActivationCount(data.data.cumulativeActivation);
            // setActivationMap(data.data.appLevelActivation);
            prepareActivationMap(data.data.appLevelActivation);
          }
        }
      })
      .catch((error) => {
        setDocumentationLink(properties.partnerDocumentationPublicLink ? properties.partnerDocumentationPublicLink : defaultDocumentationLink);
        console.error(`Partner Home: Error on checkAdvancePartner${error}`);
      });
  };

  const checkAdvancePartner = () => {
    PartnerHomeService.isAdvancePartner()
      .then((data) => {
        if (data.status) {
          if (data.data && data.data.status) {
            setDocumentationLink(properties.advanceApiDocumentationLink ? properties.advanceApiDocumentationLink : defaultDocumentationLink);
          } else {
            setDocumentationLink(properties.partnerDocumentationPublicLink ? properties.partnerDocumentationPublicLink : defaultDocumentationLink);
          }
        } else {
          setDocumentationLink(properties.partnerDocumentationPublicLink ? properties.partnerDocumentationPublicLink : defaultDocumentationLink);
        }
      })
      .catch((error) => {
        setDocumentationLink(properties.partnerDocumentationPublicLink ? properties.partnerDocumentationPublicLink : defaultDocumentationLink);
        console.error(`Partner Home: Error on checkAdvancePartner${error}`);
      });
  };

  const getUserOnboardStatus = () => {
    PartnerHomeService.getOnBoardStatus()
      .then((data) => {
        if (data.status && data.data) {
          sessionStorage.setItem("partnerOnboardingPopupShown", data.data.showOnboarding);
          setPartnerFirstOnBoardMessage(data.data.showOnboarding);
        }
      })
      .catch((error) => {
        console.error(`Partner Home: Error on getUserOnboardStatus${error}`);
      });
  };

  const setUserOnboardStatus = () => {
    PartnerHomeService.setOnBoardStatus()
      .then((data) => {
        if (data.status) {
          setPartnerFirstOnBoardMessage(false);
          sessionStorage.setItem("partnerOnboardingPopupShown", false);
        }
      })
      .catch((error) => {
        console.error(`Partner Home: Error on setUserOnboardStatus${error}`);
      });
  };

  const onSuccess = () => {
    setIsLogin(true);
  };

  useEffect(() => {
    // Only run once on mount
    console.log("Partner Dashboard App Mount");
    i18n.loadNamespaces("partners-home", (err, t) => {
      console.log(`${err} ${t}`);
      i18n.addResourceBundle("en", "partners-home", loginI18nResource);
      setShowBaseComponent(true);
      console.log("namespace loaded: ");
    });
    i18n.setDefaultNamespace("partners-home");
    if (!isLogin) {
      validateLogin(onSuccess, onError);
    }
    return () => {
      console.log("Partner Dashboard App Destroy");
    };
  }, []); // Only run once

  useEffect(() => {
    // Run when login and properties are ready
    if (isLogin && properties) {
      getAppLevelCumulativeActivation();
      checkAdvancePartner();
      const partnerOnboardingPopupShown = sessionStorage.getItem("partnerOnboardingPopupShown");
      if (partnerOnboardingPopupShown != null && partnerOnboardingPopupShown != undefined) {
        setPartnerFirstOnBoardMessage(partnerOnboardingPopupShown);
      } else {
        getUserOnboardStatus();
      }
      if (location.pathname === "/admin/apps") {
        setIsAdminLogin(true);
      }
      console.log(location.pathname);
    }
  }, [isLogin, properties]);

  return (
    showBaseComponent &&
    isLogin &&
    documentationLink && (
      <TransitionGroup>
        <Transition key={location.key} timeout={{ enter: 300, exit: 300 }} appear>
          <Provider store={globalStore}>
            <LoadingProvider>
              <Routes>
                <Route
                  key="/"
                  path="/"
                  element={
                    isLogin &&
                    properties && (
                      <Layout navigate={navigate} documentationLink={documentationLink} activationCount={activationCount} isAdminLogin={isAdminLogin}>
                        <RefreshToken />
                        <Outlet />
                      </Layout>
                    )
                  }
                >
                  <Route
                    key="homeroute"
                    index
                    // path="/home"
                    element={
                      <Suspense fallback={<>...</>}>
                        <PartnerHome
                          appData={appData}
                          i18n={i18n}
                          isPartnerDashboard
                          stepSelected={stepSelected}
                          selectStep={selectStep}
                          activationCount={activationCount}
                          activationMap={activationMap}
                          partnerFirstOnBoardMessage={partnerFirstOnBoardMessage}
                          setPartnerFirstOnBoardMessage={setPartnerFirstOnBoardMessage}
                          setUserOnboardStatus={setUserOnboardStatus}
                        />
                      </Suspense>
                    }
                  />
                  <Route path="partner/sandbox/home" element={<AppActivationSandbox appData={appData} />} />
                  <Route
                    path="partner/sandbox/apps"
                    element={
                      <AppList
                        appData={appData}
                        i18n={i18n}
                        isPartnerDashboard
                        navigateToAppDetails={navigateToAppDetails}
                        setSelectedPartnerApp={setSelectedPartnerApp}
                        navigateTo={navigateTo}
                        properties={properties}
                      />
                    }
                  />
                  <Route path="partner/onboarding/PartnerModel" element={<PartnerOnboarding appData={appData} i18n={i18n} isPartnerDashboard navigateTo={navigateTo} stepSelected={stepSelected} />} />
                  <Route
                    path="partner/sandbox/app/details/:appId"
                    element={<AppDetails appData={appData} i18n={i18n} isPartnerDashboard partnerApp={selectedPartnerApp} selectedPartnerApp={selectedPartnerApp} navigateTo={navigateTo} />}
                  />
                  <Route
                    path="/app/details/:appId/:type"
                    element={<AppDetails appData={appData} i18n={i18n} isPartnerDashboard partnerApp={selectedPartnerApp} selectedPartnerApp={selectedPartnerApp} navigateTo={navigateTo} />}
                  />
                  <Route path="firehose/trace/:appId" element={<FirehoseTrace appData={appData} i18n={i18n} isPartnerDashboard navigateTo={navigateTo} />} />
                  <Route path="rbac" element={<UserManagement appData={appData} i18n={i18n} isPartnerDashboard navigateTo={navigateTo} />} />
                  <Route path="admin/apps" element={<AdminAppCenter appData={appData} i18n={i18n} isPartnerDashboard />} />
                  <Route path="admin/apps/:appId" element={<AdminAppCenter appData={appData} i18n={i18n} isPartnerDashboard />} />
                  <Route path="partner/OAuthValidation" element={<OauthValidation appData={appData} i18n={i18n} isPartnerDashboard />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Route>
              </Routes>
            </LoadingProvider>
          </Provider>
        </Transition>
      </TransitionGroup>
    )
  );
}

export default App;
